import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { TokenSet } from '../../styles/tokens';
import { Button } from './Button';
import { Input } from './Input';
import { useLanguage } from '../../contexts/LanguageContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 18px;
  padding: 32px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const LogoImg = styled.img`
  width: 64px;
  height: 64px;
`;

const Title = styled.h2<{ color: string }>`
  font-size: 22px;
  font-weight: 800;
  color: ${p => p.color};
  text-align: center;
  margin-bottom: 6px;
`;

const Subtitle = styled.p<{ color: string }>`
  font-size: 14px;
  color: ${p => p.color};
  text-align: center;
  margin-bottom: 24px;
`;

interface Props {
  tokens: TokenSet;
  onSubmit: (name: string) => void;
}

export const NamePromptModal: React.FC<Props> = ({ tokens, onSubmit }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <Backdrop>
      <Modal tokens={tokens} onClick={e => e.stopPropagation()}>
        <LogoRow>
          <LogoImg src="/favicon.svg" alt="P4D" />
        </LogoRow>
        <Title color={tokens.text}>{t.enterNameTitle}</Title>
        <Subtitle color={tokens.textMuted}>{t.enterNameSubtitle}</Subtitle>
        <Input
          tokens={tokens}
          placeholder={t.namePlaceholder}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          autoFocus
          style={{ marginBottom: 16 }}
        />
        <Button tokens={tokens} fullWidth size="lg" onClick={handleSubmit} disabled={!name.trim()}>
          {t.joinRoomBtn}
        </Button>
      </Modal>
    </Backdrop>
  );
};
